import styled from '@emotion/styled'
import { Image, Props as ImageProps } from 'app/components/Common/Image'
import { ChevronRight, Star, Tag } from 'app/components/Icons'
import { FlexBox } from 'app/components/Layout/FlexBox'
import { useVocabularyData } from 'app/utils/vocabulary'
import { Link } from 'gatsby'
import { memo } from 'react'
import { LazyLoadComponent } from 'react-lazy-load-image-component'

export interface Props {
  className?: string
  image?: ImageProps
  languageCode: string
  price?: string
  title: string
  URL: string
  validity?: string
  variant?: Variant
}

export const Offer = memo(function Offer({
  className,
  image,
  languageCode,
  price,
  title,
  URL,
  validity,
  variant = 'default',
}: Props) {
  if (!title || !URL) {
    return null
  }

  return (
    <Container className={className} to={URL}>
      <Background className="item-bg">
        {image ? (
          <LazyLoadComponent>
            <Image {...image} />
          </LazyLoadComponent>
        ) : null}
      </Background>

      <Wrapper>
        <Title className="item-title">{title}</Title>

        {variant === 'default' ? (
          <Info>
            {validity ? (
              <Validity dial={4} row>
                <Star />
                <Label>{validity}</Label>
              </Validity>
            ) : null}
            {price ? (
              <Price dial={4} row>
                <Tag />
                <Label>{price}</Label>
              </Price>
            ) : null}
          </Info>
        ) : null}

        <CTA className="item-cta" dial={4} inline row>
          {useVocabularyData('find-out-more', languageCode)}
          <ChevronRight />
        </CTA>
      </Wrapper>
    </Container>
  )
})

const Container = styled(Link)`
  position: relative;
  text-align: left;
  &:hover {
    &:before {
      left: 4.1875rem;
    }
    .item-bg {
      img {
        transform: scale(1.1);
      }
    }
    .item-title {
      transform: translateX(2.75rem);
      &:before {
        transform: translateX(-2.75rem);
      }
    }
    .item-cta {
      color: ${({ theme }) => theme.colors.variants.neutralDark2};
      &:after {
        right: 0.3125rem;
      }
      svg {
        margin-left: 2.625rem;
        stroke: ${({ theme }) => theme.colors.variants.neutralDark2};
      }
    }
  }
  &:before {
    content: '';
    height: 100%;
    background: ${({ theme }) => theme.colors.variants.neutralLight3};
    position: absolute;
    top: 1.875rem;
    right: -1.875rem;
    left: 50%;
    transition: 0.3s ease-in-out;
    z-index: -1;
  }

  @media (max-width: 767px) {
    &:before {
      right: -1.25rem;
    }
  }
`

const Background = styled.div`
  background: ${({ theme }) => theme.colors.variants.neutralDark2};
  color: ${({ theme }) => theme.colors.variants.neutralLight4};
  overflow: hidden;
  padding-bottom: 60%;
  position: relative;

  img {
    width: 101%;
    height: 101%;
    object-fit: cover;
    position: absolute;
    top: 0;
    left: 0;
    transition: 0.3s ease-in-out;
  }

  @media (max-width: 767px) {
    padding-bottom: 75%;
  }
`

const Wrapper = styled.div`
  overflow: hidden;
  padding: 2.5rem 2.5rem 2.8125rem 0;
  position: relative;
`

const Title = styled.div`
  color: ${({ theme }) => theme.colors.variants.neutralDark2};
  font-family: ${({ theme }) => theme.fontFamily.heading};
  font-size: 1.5rem;
  font-weight: 600;
  line-height: 1.875rem;
  transition: 0.3s ease-in-out;
  &:before {
    content: '';
    width: 2rem;
    height: 0.0625rem;
    background: ${({ theme }) => theme.colors.variants.neutralDark2};
    opacity: 0.4;
    position: absolute;
    top: 0.8125rem;
    left: 0;
    transform: translateX(-2.75rem);
  }
`

const Info = styled.div``

const Label = styled.div`
  color: ${({ theme }) => theme.colors.variants.neutralDark4};
  font-family: ${({ theme }) => theme.fontFamily.paragraph};
  font-size: 0.9375rem;
  font-weight: 500;
  line-height: 1.875rem;
  opacity: 0.6;
`

const Validity = styled(FlexBox)`
  margin-top: 1.25rem;

  svg {
    width: auto;
    height: 1.125rem;
    fill: ${({ theme }) => theme.colors.variants.neutralDark4};
    margin-right: 0.625rem;
  }

  @media (max-width: 767px) {
    margin-top: 0.75rem;
  }
`

const Price = styled(FlexBox)`
  margin-top: 1.25rem;

  svg {
    width: auto;
    height: 1.125rem;
    fill: ${({ theme }) => theme.colors.variants.neutralDark4};
    stroke: ${({ theme }) => theme.colors.variants.neutralDark4};
    stroke-width: 0.7;
    margin-right: 0.625rem;
  }

  @media (max-width: 767px) {
    margin-top: 0.75rem;
  }
`

const CTA = styled(FlexBox)`
  color: ${({ theme }) => theme.colors.variants.secondaryLight};
  font-family: ${({ theme }) => theme.fontFamily.paragraph};
  font-size: 1.0625rem;
  font-weight: 500;
  line-height: 1.25rem;
  margin-top: 2.125rem;
  position: relative;
  transition: 0.3s ease-in-out;
  &:after {
    content: '';
    height: 0.125rem;
    background: ${({ theme }) => theme.colors.variants.neutralDark2};
    position: absolute;
    top: 0.625rem;
    right: 1.5625rem;
    left: calc(100% - 1.5625rem);
    transition: 0.3s 0.15s ease-in-out;
  }

  svg {
    width: auto;
    height: 1rem;
    fill: none;
    stroke: ${({ theme }) => theme.colors.variants.secondaryLight};
    stroke-width: 2;
    margin-left: 2.1875rem;
    transition: 0.3s ease-in-out;
    transform: translateY(0.0625rem);
  }

  @media (max-width: 1199px) {
    font-size: 1rem;
  }
`

export type Variant = 'default' | 'simple'
